import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

const LOG_PREFIX = '[use-capability-acls]';

export interface CapabilityUserGrant {
   id: string;
   capability_id: string;
   grantee_user_id: string;
   grantee_email?: string | null;
   scope: Record<string, unknown> | null;
   status: 'active' | 'revoked';
   granted_by: string | null;
   granted_at: string;
   revoked_at: string | null;
}

export interface CapabilityWorkerBinding {
   id: string;
   capability_id: string;
   consumer_org_id: string;
   provider_org_id: string;
   worker_oauth_client_id: string;
   status: 'active' | 'paused' | 'revoked';
   paused_at: string | null;
   revoked_at: string | null;
   created: string;
}

export interface CapabilityGrantsPage {
   items: CapabilityUserGrant[];
   total: number;
   page: number;
   page_size: number;
}

export interface CapabilityBindingsPage {
   items: CapabilityWorkerBinding[];
   total: number;
   page: number;
   page_size: number;
}

interface UseCapabilityGrantsParams {
   appKey: string;
   capabilityName: string;
   page?: number;
   pageSize?: number;
   enabled?: boolean;
}

export function useCapabilityGrants(p: UseCapabilityGrantsParams) {
   const page = p.page ?? 1;
   const pageSize = p.pageSize ?? 20;
   return useQuery<CapabilityGrantsPage>({
      enabled: p.enabled !== false && !!p.appKey && !!p.capabilityName,
      queryKey: ['capability-grants', p.appKey, p.capabilityName, page, pageSize],
      queryFn: async () => {
         const r = await api.get(
            `/applications/${p.appKey}/capabilities/${encodeURIComponent(p.capabilityName)}/grants`,
            { params: { page, page_size: pageSize } },
         );
         return r.data as CapabilityGrantsPage;
      },
   });
}

interface CreateCapabilityGrantParams {
   appKey: string;
   capabilityName: string;
}

export function useCreateCapabilityGrant(p: CreateCapabilityGrantParams) {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: async (body: {
         grantee_user_id?: string;
         grantee_email?: string;
         scope?: Record<string, unknown>;
      }) => {
         console.log(`${LOG_PREFIX}.useCreateCapabilityGrant.mutationFn - sending`, {
            appKey: p.appKey,
            capabilityName: p.capabilityName,
            body,
         });
         const r = await api.post(
            `/applications/${p.appKey}/capabilities/${encodeURIComponent(p.capabilityName)}/grants`,
            body,
         );
         return r.data as CapabilityUserGrant;
      },
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['capability-grants', p.appKey, p.capabilityName] });
      },
   });
}

interface RevokeCapabilityGrantParams {
   appKey: string;
   capabilityName: string;
}

export function useRevokeCapabilityGrant(p: RevokeCapabilityGrantParams) {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: async (grantID: string) => {
         console.log(`${LOG_PREFIX}.useRevokeCapabilityGrant.mutationFn - revoking`, {
            appKey: p.appKey,
            capabilityName: p.capabilityName,
            grantID,
         });
         await api.delete(
            `/applications/${p.appKey}/capabilities/${encodeURIComponent(p.capabilityName)}/grants/${grantID}`,
         );
      },
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['capability-grants', p.appKey, p.capabilityName] });
      },
   });
}

interface UseCapabilityBindingsParams {
   appKey: string;
   capabilityName: string;
   page?: number;
   pageSize?: number;
   enabled?: boolean;
}

export function useCapabilityBindings(p: UseCapabilityBindingsParams) {
   const page = p.page ?? 1;
   const pageSize = p.pageSize ?? 20;
   return useQuery<CapabilityBindingsPage>({
      enabled: p.enabled !== false && !!p.appKey && !!p.capabilityName,
      queryKey: ['capability-bindings', p.appKey, p.capabilityName, page, pageSize],
      queryFn: async () => {
         const r = await api.get(
            `/applications/${p.appKey}/capabilities/${encodeURIComponent(p.capabilityName)}/bindings`,
            { params: { page, page_size: pageSize } },
         );
         return r.data as CapabilityBindingsPage;
      },
   });
}

export type BindingAction = 'pause' | 'resume' | 'revoke';

interface UpdateBindingParams {
   appKey: string;
   capabilityName: string;
}

export function useUpdateCapabilityBinding(p: UpdateBindingParams) {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: async (args: { bindingID: string; action: BindingAction }) => {
         console.log(`${LOG_PREFIX}.useUpdateCapabilityBinding.mutationFn - acting`, {
            appKey: p.appKey,
            capabilityName: p.capabilityName,
            bindingID: args.bindingID,
            action: args.action,
         });
         const r = await api.patch(
            `/applications/${p.appKey}/capabilities/${encodeURIComponent(p.capabilityName)}/bindings/${args.bindingID}`,
            { action: args.action },
         );
         return r.data as CapabilityWorkerBinding;
      },
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['capability-bindings', p.appKey, p.capabilityName] });
      },
   });
}
